/**
 * useCheckLocation
 * Where a check is, for its modals: the one component it is about as a
 * work-order component row ("Hitta komponenterna" images, GPS, link to the
 * component page), or the marker of a check linked to a place. Read-only.
 * Skickbedömning (a group, no `componentKey`) and building-wide checks get
 * neither.
 */
import { useEffect, useMemo, useState } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { BaseComponent, Building, BuildingModel, BuildingSummary } from '../../types/protocols';
import { BuildingCheck } from '../../Pages/BuildingChecks/buildingChecksUtils';
import { checkLocationMarker } from '../../Pages/BuildingChecks/checkLocationMarker';
import { checkComponentName } from './useCheckName';
import { componentLocationLabel, componentLocationSubtitle } from '../../Helpers/componentLocationLabel';
import { useComponentLocationImages } from '../workOrders/useComponentLocationImages';
import { resolveFirebaseImageUrl } from '../../Helpers/ImageLoader';
import { ComponentListItem, ComponentLocationImage } from '../../Pages/WorkOrders/components/shared';

export type CheckLocationRef = Pick<BuildingCheck, 'componentKey' | 'location'>;

export interface CheckLocation {
    /** The check's component as a row; empty without one or while loading. */
    componentItems: ComponentListItem[];
    /** The check's component no longer exists (or is archived). */
    componentMissing: boolean;
    /** The marker of a check linked to a place; null without one. */
    locationImage: ComponentLocationImage | null;
    loading: boolean;
}

export const componentPageHref = (buildingKey: string, componentKey: string): string =>
    `/building/${buildingKey}/component/${componentKey}`;

/**
 * @param building the check's building when the page already has it loaded
 *                 (with its model); otherwise it is fetched once.
 */
export function useCheckLocation(
    buildingKey: string | null | undefined,
    check: CheckLocationRef | null | undefined,
    building?: Building | null
): CheckLocation {
    const componentKey = check?.componentKey;
    const location = check?.location;
    const wanted = Boolean(buildingKey && (componentKey || location));
    const given = building && building.key === buildingKey && building.model ? building : null;

    const [fetched, setFetched] = useState<Building | null>(null);
    const [fetching, setFetching] = useState(false);

    useEffect(() => {
        if (!wanted || given || !buildingKey || fetched?.key === buildingKey) return;
        let cancelled = false;
        setFetching(true);
        get(ref(getDatabase(), `buildings/${buildingKey}`))
            .then((snapshot) => {
                if (cancelled) return;
                setFetched(snapshot.exists()
                    ? { key: buildingKey, model: snapshot.val() as BuildingModel, summary: { key: buildingKey } as BuildingSummary }
                    : null);
            })
            .catch((err) => console.warn('Could not load building for the check location', err))
            .finally(() => { if (!cancelled) setFetching(false); });
        return () => { cancelled = true; };
    }, [wanted, given, buildingKey, fetched?.key]);

    const source = given ?? (fetched?.key === buildingKey ? fetched : null);

    const component: BaseComponent | undefined = componentKey
        ? source?.model.structure?.components?.[componentKey]
        : undefined;
    const liveComponent = component && !component.archivedAt ? component : undefined;

    const components = useMemo(
        () => (componentKey && liveComponent ? { [componentKey]: liveComponent } : {}),
        [componentKey, liveComponent]
    );
    const componentKeys = useMemo(() => (componentKey && liveComponent ? [componentKey] : []), [componentKey, liveComponent]);
    const locationImages = useComponentLocationImages(source, components, componentKeys);

    const componentItems = useMemo((): ComponentListItem[] => {
        if (!buildingKey || !componentKey || !liveComponent || !source) return [];
        const gps = liveComponent.gpsCoordinates?.value;
        const locations = locationImages[componentKey];
        return [{
            key: componentKey,
            name: checkComponentName(liveComponent) || 'Komponent',
            subtitle: componentLocationSubtitle(componentLocationLabel(liveComponent, source)) ?? undefined,
            href: componentPageHref(buildingKey, componentKey),
            locations: locations && locations.length > 0 ? locations : undefined,
            gps: gps?.latitude && gps?.longitude ? { lat: gps.latitude, lng: gps.longitude } : undefined,
        }];
    }, [buildingKey, componentKey, liveComponent, source, locationImages]);

    // The place marker: one image, its URL resolved here.
    const marker = useMemo(
        () => (!componentKey && source ? checkLocationMarker(location, source) : null),
        [componentKey, location, source]
    );
    const [markerUrl, setMarkerUrl] = useState<{ path: string; url: string | null } | null>(null);
    useEffect(() => {
        const path = marker?.storagePath;
        if (!path || markerUrl?.path === path) return;
        let cancelled = false;
        resolveFirebaseImageUrl(path)
            .then((url) => { if (!cancelled) setMarkerUrl({ path, url }); })
            .catch((err) => {
                console.warn('Could not resolve check location image', path, err);
                if (!cancelled) setMarkerUrl({ path, url: null });
            });
        return () => { cancelled = true; };
    }, [marker?.storagePath, markerUrl?.path]);

    const locationImage = useMemo((): ComponentLocationImage | null => {
        if (!marker) return null;
        const resolved = marker.storagePath && markerUrl?.path === marker.storagePath;
        return {
            label: marker.label,
            imageUrl: resolved ? markerUrl!.url : null,
            loading: Boolean(marker.storagePath) && !resolved,
            x: marker.x,
            y: marker.y,
        };
    }, [marker, markerUrl]);

    return {
        componentItems,
        componentMissing: Boolean(componentKey && source && !liveComponent),
        locationImage,
        loading: wanted && !source && fetching,
    };
}
